import * as Cesium from "cesium";

import {
  DISTANCE_VIEW_LOGICAL_RANGE_MAX_METERS,
  HEIGHT_SENSITIVITY,
  HEIGHT_VIEW_LOGICAL_MAX_RELATIVE_HEIGHT_METERS,
  HEIGHT_VIEW_LOGICAL_MIN_RELATIVE_HEIGHT_METERS,
  HEIGHT_VIEW_LOGICAL_RANGE_MAX_METERS,
  HEIGHT_VIEW_LOGICAL_RANGE_MIN_METERS,
} from "./measurementConstants";
import type { MeasurementType } from "@/stores/measurementStore";

export const findGroundPointAtScreenPosition = (
  viewer: Cesium.Viewer,
  screenPosition: Cesium.Cartesian2,
): Cesium.Cartesian3 | null => {
  const ray = viewer.camera.getPickRay(screenPosition);
  if (!ray) return null;

  const pointOnTerrain = viewer.scene.globe.pick(ray, viewer.scene);
  if (pointOnTerrain) {
    return pointOnTerrain;
  }

  const fallbackEllipsoid = viewer.camera.pickEllipsoid(
    screenPosition,
    viewer.scene.globe.ellipsoid,
  );
  return fallbackEllipsoid ?? null;
};

export const computeMetersPerPixelAtPoint = (
  viewer: Cesium.Viewer,
  point: Cesium.Cartesian3,
): number => {
  const cameraDistance = Cesium.Cartesian3.distance(
    viewer.camera.positionWC,
    point,
  );
  const canvasHeight = Math.max(1, viewer.scene.canvas.clientHeight);

  let fov = Cesium.Math.toRadians(60);
  if (viewer.camera.frustum instanceof Cesium.PerspectiveFrustum) {
    fov = viewer.camera.frustum.fovy ?? fov;
  } else if (
    viewer.camera.frustum instanceof Cesium.PerspectiveOffCenterFrustum
  ) {
    const frustum = viewer.camera.frustum;
    const top = frustum.top ?? 1;
    const bottom = frustum.bottom ?? -1;
    fov = top - bottom;
  }

  const metersPerPixel =
    (2 * cameraDistance * Math.tan(Math.max(0.01, fov) / 2)) / canvasHeight;
  return Math.max(0.01, metersPerPixel * HEIGHT_SENSITIVITY);
};

export const computeHeightDistanceFromScreenDelta = (payload: {
  viewer: Cesium.Viewer;
  startPoint: Cesium.Cartesian3;
  initialScreenY: number;
  currentScreenY: number;
}): { endPoint: Cesium.Cartesian3; distanceMeters: number } => {
  const metersPerPixel = computeMetersPerPixelAtPoint(
    payload.viewer,
    payload.startPoint,
  );
  const verticalPixels = payload.initialScreenY - payload.currentScreenY;
  const heightDeltaMeters = Math.max(0, verticalPixels * metersPerPixel);

  const startCartographic = Cesium.Cartographic.fromCartesian(
    payload.startPoint,
  );
  const endPoint = Cesium.Cartesian3.fromRadians(
    startCartographic.longitude,
    startCartographic.latitude,
    startCartographic.height + heightDeltaMeters,
  );

  return {
    endPoint,
    distanceMeters: heightDeltaMeters,
  };
};

export const projectToWindow = (
  viewer: Cesium.Viewer,
  point: Cesium.Cartesian3,
): Cesium.Cartesian2 | null => {
  const projected = Cesium.SceneTransforms.worldToWindowCoordinates(
    viewer.scene,
    point,
  );
  return projected ?? null;
};

export const computeHeightDistanceFromCursor = (payload: {
  viewer: Cesium.Viewer;
  startPoint: Cesium.Cartesian3;
  cursorScreenY: number;
  fallbackStartScreenY: number | null;
}): { endPoint: Cesium.Cartesian3; distanceMeters: number } => {
  const startCartographic = Cesium.Cartographic.fromCartesian(payload.startPoint);
  const startWindow = projectToWindow(payload.viewer, payload.startPoint);
  const startY =
    startWindow?.y ?? payload.fallbackStartScreenY ?? payload.cursorScreenY;

  // Curseur sous le point de base => on reste au sol (pas de hauteur négative).
  if (payload.cursorScreenY >= startY) {
    return {
      endPoint: Cesium.Cartesian3.clone(payload.startPoint),
      distanceMeters: 0,
    };
  }

  const projectedYAtHeight = (heightDeltaMeters: number): number | null => {
    const pointAtHeight = Cesium.Cartesian3.fromRadians(
      startCartographic.longitude,
      startCartographic.latitude,
      startCartographic.height + heightDeltaMeters,
    );
    const projected = projectToWindow(payload.viewer, pointAtHeight);
    return projected?.y ?? null;
  };

  let low = 0;
  let high = 5;
  let yHigh = projectedYAtHeight(high);

  while (high < 5000 && yHigh !== null && yHigh > payload.cursorScreenY) {
    low = high;
    high *= 2;
    yHigh = projectedYAtHeight(high);
  }

  // Fallback si la projection échoue ponctuellement.
  if (yHigh === null) {
    return computeHeightDistanceFromScreenDelta({
      viewer: payload.viewer,
      startPoint: payload.startPoint,
      initialScreenY: startY,
      currentScreenY: payload.cursorScreenY,
    });
  }

  if (yHigh > payload.cursorScreenY) {
    high = 5000;
  }

  for (let i = 0; i < 16; i += 1) {
    const mid = (low + high) / 2;
    const yMid = projectedYAtHeight(mid);
    if (yMid === null) break;

    if (yMid > payload.cursorScreenY) {
      low = mid;
    } else {
      high = mid;
    }
  }

  const resolvedHeight = Math.max(0, high);
  const endPoint = Cesium.Cartesian3.fromRadians(
    startCartographic.longitude,
    startCartographic.latitude,
    startCartographic.height + resolvedHeight,
  );

  return {
    endPoint,
    distanceMeters: resolvedHeight,
  };
};

export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

export const normalizeAngle = (angle: number): number =>
  Cesium.Math.negativePiToPi(angle);

export const getViewerCenterGroundPoint = (
  viewer: Cesium.Viewer,
): Cesium.Cartesian3 | null => {
  const canvas = viewer.scene.canvas;
  const center = new Cesium.Cartesian2(
    canvas.clientWidth / 2,
    canvas.clientHeight / 2,
  );
  return findGroundPointAtScreenPosition(viewer, center);
};

export const isCameraViewLogicalForMeasurement = (payload: {
  viewer: Cesium.Viewer;
  measurementType: MeasurementType;
  targetPitch: number;
  pitchTolerance: number;
}): boolean => {
  const pitchOk =
    Math.abs(
      normalizeAngle(payload.viewer.camera.pitch - payload.targetPitch),
    ) <= payload.pitchTolerance;
  if (!pitchOk) return false;

  const centerPoint = getViewerCenterGroundPoint(payload.viewer);
  if (!centerPoint) {
    // Sans point exploitable, on ne force pas de mouvement inutile.
    return true;
  }

  const distanceToCenter = Cesium.Cartesian3.distance(
    payload.viewer.camera.positionWC,
    centerPoint,
  );

  if (payload.measurementType === "distance-ground") {
    return distanceToCenter <= DISTANCE_VIEW_LOGICAL_RANGE_MAX_METERS;
  }

  const cameraCarto = Cesium.Cartographic.fromCartesian(
    payload.viewer.camera.positionWC,
  );
  const centerCarto = Cesium.Cartographic.fromCartesian(centerPoint);
  const relativeHeight = cameraCarto.height - centerCarto.height;

  return (
    distanceToCenter >= HEIGHT_VIEW_LOGICAL_RANGE_MIN_METERS &&
    distanceToCenter <= HEIGHT_VIEW_LOGICAL_RANGE_MAX_METERS &&
    relativeHeight >= HEIGHT_VIEW_LOGICAL_MIN_RELATIVE_HEIGHT_METERS &&
    relativeHeight <= HEIGHT_VIEW_LOGICAL_MAX_RELATIVE_HEIGHT_METERS
  );
};
